import { useEffect, useMemo, useState } from 'react';

import { Field, useFormikContext } from 'formik';
import { v4 as uuidv4 } from 'uuid';

import DynamicPropertyMain from '../DynamicProperty/DynamicPropertyMain';
import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';
import projectService from '../../service/project.service';
import { FormErrorUI, FormLabelUI, InputUI, TabsUI, FileTabUI, DeleteDialogUI, CheckboxUI } from '../Interface';
import ProjectCommunication from './ProjectCommunication';
import ProjectContact from './ProjectContact';
import ProjectResourceOneTable from './Resources/ResourceOneTable';
import ProjectResourceTwoTable from './Resources/ResourceTwoTable';
import ProjectResourceThreeTable from './Resources/ResourceThreeTable';
import ProjectResourceFourTable from './Resources/ResourceFourTable';
import ProjectResourceFiveTable from './Resources/ResourceFiveTable';
import ProjectHistory from './ProjectHistory/ProjectHistory';
import CheckLists from '../CheckList/CheckListTab/CheckLists';
import ProjectTaskList from '../TaskList/TaskListTab/ProjectTaskList';
import useSaveView from '../../hooks/useSaveView';
import useView from '../../hooks/useView';
import ExcelImportTab from '../Interface/ExcelImportTab';
import jokerService from '../../service/joker.service';
import { JokerTypes } from '../../config/joker';
import JokerTab from '../Interface/Joker/JokerTab';
import ProjectPriceTable from './ProjectPriceTable';
import Comments from '../Comment/Comments';
import ProjectEmployeeTable from './ProjectEmployeeTable';
import useParam from '../../context/ParamContext';
import ProjectPaymentScheduleTable from './ProjectPaymentScheduleTable';
import ProjectSubcontractorsTable from './ProjectSubcontractorsTable';
import ProjectEmployeeAttendanceDetailsTable from './ProjectEmployeeAttendanceDetailsTable';
import ProjectExtraWorkSimpleTasksTable from './ProjectExtraWorkSimpleTasks/ProjectExtraWorkSimpleTasksTable';
import { FormType } from '../../config/form';
import ProjectSimpleTasksTable from './ProjectSimpleTasksTable';

const PropertyGroups = (props) => {
  const { propertyGroups, groupState, user } = props;
  const { group, setGroup } = groupState;
  const saveView = useSaveView(user);
  const savedGroups = useView('projectPage', user);

  const handleCheckboxChange = (id) => {
    setGroup((prevGroup) => {
      const isSelected = prevGroup.id.includes(id);
      const updatedIds = isSelected ? prevGroup.id.filter((groupId) => groupId !== id) : [...prevGroup.id, id];

      const newGroup = { ...prevGroup, id: updatedIds };

      saveView('projectPage', { dynGroup: updatedIds });

      return newGroup;
    });
  };

  useEffect(() => {
    if (Object.keys(savedGroups).length > 0) {
      setGroup((prevGroup) => ({
        ...prevGroup,
        id: savedGroups.dynGroup,
      }));
    }
  }, [savedGroups]);

  return (
    <div className="flex -mt-5 flex-wrap">
      {propertyGroups.length > 0 &&
        propertyGroups.map((groupItem) => (
          <div key={groupItem.id}>
            <CheckboxUI
              label={groupItem.name}
              checked={group.id.includes(groupItem.id)}
              onChange={() => handleCheckboxChange(groupItem.id)}
            />
          </div>
        ))}
    </div>
  );
};

const DescriptionPanel = ({ disabled, errors, values }) => {
  return (
    <div>
      <div className="col-span-full relative">
        <div className="mt-2">
          <Field
            type="text"
            name="projectDescription"
            as={InputUI}
            className={`${disabled ? 'rounded-md' : ''} resize-none h-36, p-2`}
            fullWidth
            label={<FormLabelUI text="Leírás" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.projectDescription} />}
            disabled={disabled}
            InputLabelProps={{ shrink: values.projectDescription !== '' }}
            multiline
            rows={10}
          />
        </div>
      </div>
    </div>
  );
};

const ProjectTabs = (props) => {
  const { setFieldValue, values } = useFormikContext();
  const { params } = useParam();
  const { user, selectedValues, setSelectedValues, disabled, setRefetchTable, addField, dynLoadingState, dialog } =
    props;
  const { setDynLoading } = dynLoadingState;
  const [activeTab, setActiveTab] = useState(0);
  const [deleteDialog, setDeletDialog] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [jokerGroups, setJokerGroups] = useState([]);

  useEffect(() => {
    getJokerGroups();
  }, []);

  const handleUpload = (files) => {
    const filesWithId = files.map((file) => {
      file.id = uuidv4();
      return file;
    });

    const existingAttachments = Array.isArray(values.projectAttachment) ? values.projectAttachment : [];

    setFieldValue('projectAttachment', [...existingAttachments, ...filesWithId]);
  };

  const hideDeleteDialog = () => {
    setDeletDialog(false);
  };

  const handleRemoveElement = () => {
    setFieldValue(
      'projectAttachment',
      (values.projectAttachment || []).filter((projectAttachment) => projectAttachment.id !== fileToDelete.id)
    );

    setFileToDelete(null);
    hideDeleteDialog();
  };

  const getJokerGroups = async () => {
    jokerService.getGroups(JokerTypes.PROJECT).then((data) => {
      setJokerGroups(data);
    });
  };

  const tabs = [
    {
      id: 0,
      name: 'Tulajdonságok',
      content: (
        <div>
          <PropertyGroups {...props} />
          <DynamicPropertyMain {...props} />
        </div>
      ),
      key: 'PROPERTIES',
    },
    {
      id: 1,
      name: 'Checklista',
      content: <CheckLists {...props} />,
      key: 'CHECKLIST',
    },
    {
      id: 2,
      name: 'Feladat lista',
      content: <ProjectTaskList {...props} />,
      key: 'TASKLIST',
    },
    {
      id: 3,
      name: 'Pótmunka',
      content: <ProjectTaskList {...props} isExtraWorkType={true} />,
      key: 'EXTRAWORK',
    },
    {
      id: 4,
      name: 'Megjegyzés',
      content: <DescriptionPanel {...props} />,
      key: 'DESCRIPTION',
    },
    {
      id: 5,
      name: 'Hozzászólások',
      content: (
        <Comments
          rows={selectedValues.comments ?? []}
          lineId={selectedValues.projectId}
          subMenuUrl="Project"
          setRefetchTable={dialog ? dialog?.setTaskChanged : setRefetchTable}
          disabled={disabled || !addField}
        />
      ),
      key: 'COMMENT',
    },
    {
      id: 6,
      name: 'Projekt tagok',
      content: <ProjectEmployeeTable {...props} />,
      key: 'PROJECT_EMPLOYEE',
    },
    {
      id: 7,
      name: props.submenuNames[0],
      content: <ProjectResourceOneTable {...props} />,
      key: 'RESOURCE_ONE',
    },
    {
      id: 8,
      name: props.submenuNames[1],
      content: <ProjectResourceTwoTable {...props} />,
      key: 'RESOURCE_TWO',
    },
    {
      id: 9,
      name: props.submenuNames[2],
      content: <ProjectResourceThreeTable {...props} />,
      key: 'RESOURCE_THREE',
    },
    {
      id: 10,
      name: 'Kapcsolattartó',
      content: <ProjectContact {...props} />,
      key: 'CONTACT',
    },
    {
      id: 11,
      name: 'Csatolmányok',
      content: (
        <FileTabUI
          disabled={disabled}
          savedFiles={[
            ...(values?.projectAttachment ?? []),
            ...(props.selectedValues.taskLists ?? []).flatMap((taskList) => taskList.taskListAttachment ?? []),
            ...(params.PROJECT_SHOW_EMPLOYEEATTENDANCE_ATTACHMENTS === 'Y'
              ? props.selectedValues.employeeAttendanceAttachment ?? []
              : []),
          ]}
          parentObj={FormType.PROJECT}
          handleUpload={handleUpload}
          handleDelete={(file) => {
            setFileToDelete(file);
            setDeletDialog(true);
          }}
        />
      ),
      key: 'ATTACHMENT',
    },
    {
      id: 12,
      name: 'Kommunikáció',
      content: <ProjectCommunication {...props} />,
      key: 'COMMUNICATION',
    },
    {
      id: 13,
      name: props.submenuNames[3],
      content: <ProjectResourceFourTable {...props} />,
      key: 'RESOURCE_FOUR',
    },
    {
      id: 14,
      name: props.submenuNames[4],
      content: <ProjectResourceFiveTable {...props} />,
      key: 'RESOURCE_FIVE',
    },
    {
      id: 15,
      name: 'Projekt történet',
      content: <ProjectHistory {...props} />,
      key: 'PROJECT_HISTORY',
    },
    {
      id: 16,
      name: 'Adat import',
      content: <ExcelImportTab {...props} />,
      key: 'PROJECT_IMPORT',
    },
    {
      id: 17,
      name: 'Szerződött árak',
      content: <ProjectPriceTable {...props} />,
      key: 'PROJECT_PRICE',
    },
    {
      id: 18,
      name: 'Fizetési ütemezés',
      content: <ProjectPaymentScheduleTable {...props} />,
      key: 'PAYMENT_SCHEDULE',
    },
    {
      id: 19,
      name: 'Alvállalkozók',
      content: <ProjectSubcontractorsTable {...props} />,
      key: 'SUBCONTRACTORS',
    },
    {
      id: 20,
      name: 'Dolgozói jelenlétek',
      content: <ProjectEmployeeAttendanceDetailsTable {...props} />,
      key: 'EMPLOYEE_ATTENDANCE_DETAILS',
    },
    {
      id: 21,
      name: 'Pótmunka',
      content: <ProjectExtraWorkSimpleTasksTable {...props} />,
      key: 'EXTRA_WORK_SIMPLETASKS',
    },
    {
      id: 22,
      name: 'Feladatok',
      content: <ProjectSimpleTasksTable {...props} />,
      key: 'PROJECT_SIMPLETASKS',
    },
    ...((jokerGroups.length > 0 &&
      jokerGroups
        .filter((group) => group.jokerFields.length > 0)
        .map((group, index) => ({
          id: index + 23,
          name: group.name,
          key: `JOKER_${group.id}`,
          content: <JokerTab fields={group.jokerFields} key={index} {...props} />,
        }))) ||
      []),
  ];

  const visibleTabs = useMemo(() => {
    const visibilityConfig = JSON.parse(params.PROJECT_TAB_FIELDS || '[]');
    const orderConfig = JSON.parse(params.PROJECT_TAB_ORDER || '[]');
    const disabledWithoutEditPermession = JSON.parse(params.PROJECT_DISABLE_TABS_WITHOUT_EDIT_PERMISSION || '[]');

    const tabVisibilityMap = Object.assign({}, ...(visibilityConfig || []));
    const tabOrderMap = Object.assign({}, ...(orderConfig || []));

    const filteredTabs = tabs.filter((tab) => {
      if (!props.canEdit && props.infoPanel && disabledWithoutEditPermession.includes(tab.key)) {
        return false;
      }
      if (!params.PROJECT_TAB_FIELDS) {
        return true;
      }
      if (tab.key.startsWith('RESOURCE_')) {
        const resourceIndex = parseInt(tab.key.split('_')[1]) - 1;
        return (
          (!tabVisibilityMap[tab.key] || tabVisibilityMap[tab.key] === 'Y') &&
          props.submenuNames[resourceIndex] !== null
        );
      }

      return !tabVisibilityMap[tab.key] || tabVisibilityMap[tab.key] === 'Y';
    });

    const ordered = [];
    const unordered = [];

    filteredTabs.forEach((tab) => {
      if (tabOrderMap[tab.key] !== undefined) {
        ordered.push({
          ...tab,
          visOrder: tabOrderMap[tab.key],
        });
      } else {
        unordered.push(tab);
      }
    });

    ordered.sort((a, b) => a.visOrder - b.visOrder);

    return [...ordered, ...unordered];
  }, [params.PROJECT_TAB_FIELDS, params.PROJECT_TAB_ORDER, tabs, props.submenuNames]);

  const handleTabClick = (tabIndex) => {
    setActiveTab(tabIndex);
  };

  useEffect(() => {
    const hasPropertiesTab = visibleTabs.some((tab) => tab.key === 'PROPERTIES');

    if (!hasPropertiesTab) {
      setDynLoading(false);
    }
  }, [visibleTabs]);

  return (
    <div className="my-4 mt-6">
      <div className="px-3">
        <TabsUI activeTab={activeTab} handleTabClick={handleTabClick}>
          {visibleTabs.map((tab, index) => (
            <div key={index} name={tab.name}>
              {tab.content}
            </div>
          ))}
        </TabsUI>
      </div>
      <DeleteDialogUI
        handleRemoveElement={handleRemoveElement}
        show={deleteDialog}
        onHide={() => {
          hideDeleteDialog();
          setFileToDelete(null);
        }}
      />
    </div>
  );
};

export default ProjectTabs;
